import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen } from '@fortawesome/free-solid-svg-icons';

const LoginPage = () => {
  const { user, loading, loginWithGoogle, loginWithTwitter } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authError, setAuthError] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    // Check for auth error from OAuth callback
    const error = searchParams.get('auth_error');
    if (error) {
      setAuthError(decodeURIComponent(error));
      // Clear the error from URL
      window.history.replaceState({}, '', '/login');
    }
    
    // Check for session token in URL (cross-domain OAuth)
    const sessionToken = searchParams.get('session_token');
    if (sessionToken) {
      // Set the session token as a cookie on this domain
      document.cookie = `session_token=${sessionToken}; path=/; max-age=${30*24*60*60}; secure; samesite=lax`;
      console.log('Cross-domain OAuth: Set session cookie from URL token');
      // Clear the token from URL and redirect
      window.location.replace('/app');
      return;
    }
    
    // Check for auth success - force a full reload to pick up the new cookie
    if (searchParams.get('auth') === 'success') {
      // Remove the query param and do a fresh navigation to /app
      // The full page reload ensures cookies are read properly
      window.location.replace('/app');
      return;
    }
    
    // If already logged in, redirect to app
    if (user && !loading) {
      navigate('/app');
    }
  }, [user, loading, navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="border border-slate-200 shadow-lg bg-white rounded-lg">
          <div className="pt-8 pb-8 px-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faFolderOpen} className="h-9 w-9 text-orange-500" style={{ color: '#F58346' }} />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">
                Welcome to <span className="text-slate-800">TICK<span className="text-orange-500">L</span>ER</span>
              </h1>
              <p className="text-slate-600">IDEAS WORTH VETTING.</p>
            </div>
            
            {/* Show auth error if any */}
            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">Login failed: {authError}</p>
              </div>
            )}
            
            <div className="space-y-4">
              {/* Terms Agreement Checkbox - Simple and unobtrusive */}
              <label htmlFor="terms-agreement" className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  id="terms-agreement"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="h-4 w-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500 cursor-pointer"
                />
                <span className="whitespace-nowrap">
                  I agree to the{' '}
                  <Link to="/terms" className="text-orange-600 hover:underline" target="_blank">Terms of Use</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-orange-600 hover:underline" target="_blank">Privacy Policy</Link>
                </span>
              </label>
              
              {/* Sign in with X - Primary login option per X Brand Guidelines */}
              <button 
                onClick={() => { 
                  if (!agreedToTerms) {
                    setAuthError('Please agree to the Terms of Use and Privacy Policy to continue.');
                    return;
                  }
                  setAuthError(null); 
                  // Store agreement timestamp in localStorage before OAuth redirect
                  localStorage.setItem('terms_agreed_at', new Date().toISOString());
                  loginWithTwitter(); 
                }}
                disabled={!agreedToTerms}
                data-testid="sign-in-with-x-btn"
                className={`w-full py-3.5 font-semibold rounded-md transition-colors flex items-center justify-center gap-3 text-base ${
                  agreedToTerms 
                    ? 'bg-black hover:bg-gray-900 text-white cursor-pointer' 
                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                }`}
              >
                {/* X Logo */}
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-label="X logo">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Sign in with X
              </button>
              
              {/* Create X Account link for users without X account */}
              <div className="text-center">
                <p className="text-xs text-slate-500">
                  Don't have an X account?{' '}
                  <a 
                    href="https://x.com/i/flow/signup" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-black hover:underline font-medium inline-flex items-center gap-1"
                  >
                    Create one on X
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                  </a>
                </p>
              </div>
              
              {/* TEMPORARILY HIDDEN - MVP simplification. X login auto-connects account. Will add back when adding more import methods (email, bookmarklet) */}
              {false && (
                <button 
                  onClick={() => {
                    if (!agreedToTerms) {
                      setAuthError('Please agree to the Terms of Use and Privacy Policy to continue.');
                      return;
                    }
                    localStorage.setItem('terms_agreed_at', new Date().toISOString());
                    loginWithGoogle();
                  }}
                  disabled={!agreedToTerms}
                  className={`w-full py-3 font-medium rounded-md transition-colors flex items-center justify-center ${
                    agreedToTerms 
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white cursor-pointer' 
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
              )}
            </div>
            
            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-3">What you'll get:</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Track investment ideas and research
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Organize tickers and build watchlists
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Schedule research reminders
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Get timely SEC filings when you need them
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  1,000 saved posts per month included
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <a href="https://trytickler.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Product Overview</a>
                </li>
              </ul>
              <p className="mt-4 text-xs text-slate-500">
                Need more? <a href="mailto:support@trytickler.com?subject=Enterprise%20Plan%20Inquiry" className="text-blue-600 underline">Contact us</a> for Enterprise plans with unlimited access.
              </p>
            </div>
          </div>
        </div>

        {/* Featured on TerminalValue.io badge */}
        <div className="mt-6 flex justify-center" data-testid="terminalvalue-badge-container">
          <a
            href="https://terminalvalue.io/tool/tickler"
            target="_blank"
            rel="noopener"
            data-testid="terminalvalue-badge-link"
          >
            <img
              src="https://terminalvalue.io/api/badges/tickler.svg?style=standard"
              alt="Featured on TerminalValue.io"
              width="230"
              height="54"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
